/**
 * Admin Opponents Page
 * Browse and manage opponent schools imported from LeagueOS
 * 🎯 Scouting Database
 */

import { useState, useEffect } from 'react';
import { db } from '../lib/firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';

// Game format labels
const FORMAT_LABELS: Record<string, string> = {
    'ssbu': 'SSBU',
    'solos': 'Solos',
    '3crewbattles': 'Crews',
};

interface OpponentPlayer {
    id: string;
    gamerTag: string;
    firstName: string | null;
    lastName: string | null;
    fullName: string | null;
    wins: number;
    losses: number;
    gameWins: number;
    gameLosses: number;
    elo: number;
    knockOuts: number;
    falls: number;
    discordName: string | null;
    teams: string[];
}

interface Opponent {
    id: string;
    name: string;
    nameShort: string | null;
    tag: string | null;
    location: string | null;
    teamCount: number;
    games: string[];
    formats: string[];
    totalWins: number;
    totalLosses: number;
    winRate: number;
    season: string;
}

export default function AdminOpponentsPage() {
    const [opponents, setOpponents] = useState<Opponent[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedOpponent, setSelectedOpponent] = useState<Opponent | null>(null);
    const [players, setPlayers] = useState<OpponentPlayer[]>([]);
    const [loadingPlayers, setLoadingPlayers] = useState(false);

    // Load opponents
    useEffect(() => {
        async function loadOpponents() {
            try {
                const snap = await getDocs(
                    query(collection(db, 'opponents'), orderBy('name'))
                );
                const data = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                })) as Opponent[];
                setOpponents(data);
            } catch (err) {
                console.error('Error loading opponents:', err);
            } finally {
                setLoading(false);
            }
        }
        loadOpponents();
    }, []);

    // Load players when opponent selected
    useEffect(() => {
        if (!selectedOpponent) {
            setPlayers([]);
            return;
        }

        async function loadPlayers() {
            setLoadingPlayers(true);
            try {
                const snap = await getDocs(
                    query(
                        collection(db, 'opponents', selectedOpponent!.id, 'players'),
                        orderBy('gamerTag')
                    )
                );
                const data = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                })) as OpponentPlayer[];
                setPlayers(data);
            } catch (err) {
                console.error('Error loading players:', err);
            } finally {
                setLoadingPlayers(false);
            }
        }
        loadPlayers();
    }, [selectedOpponent]);

    // Filter opponents
    const filteredOpponents = opponents.filter(o =>
        o.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.tag?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        o.location?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="p-6 lg:p-8 max-w-6xl">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-white">🎯 Opponent Database</h1>
                <p className="text-secondary-400">
                    {opponents.length} schools imported from LeagueOS (Fall 2025)
                </p>
            </div>

            {/* Search */}
            <div className="mb-6">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by school name, tag, or location..."
                    className="w-full px-4 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-500 focus:outline-none focus:border-primary-500"
                />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                {/* Opponent List */}
                <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
                    {filteredOpponents.length === 0 ? (
                        <div className="card p-8 text-center text-secondary-400">
                            {searchQuery ? 'No opponents match your search' : 'No opponents imported yet'}
                        </div>
                    ) : (
                        filteredOpponents.map(opponent => (
                            <button
                                key={opponent.id}
                                onClick={() => setSelectedOpponent(opponent)}
                                className={`w-full card text-left transition-all ${selectedOpponent?.id === opponent.id
                                    ? 'ring-2 ring-primary-500 bg-primary-500/10'
                                    : 'hover:bg-secondary-700/50'
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="min-w-0">
                                        <h3 className="font-medium text-white truncate">
                                            {opponent.name}
                                        </h3>
                                        <div className="flex items-center gap-2 text-sm text-secondary-400">
                                            {opponent.tag && (
                                                <span className="font-mono text-xs bg-secondary-700 px-1 rounded">
                                                    {opponent.tag}
                                                </span>
                                            )}
                                            {opponent.location && (
                                                <span>{opponent.location}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-sm">
                                            <span className="text-green-400">{opponent.totalWins}W</span>
                                            <span className="text-secondary-500"> - </span>
                                            <span className="text-red-400">{opponent.totalLosses}L</span>
                                        </div>
                                        <div className="text-xs text-secondary-500">
                                            {opponent.teamCount} team{opponent.teamCount !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))
                    )}
                </div>

                {/* Player Detail Panel */}
                <div className="card bg-secondary-800/50">
                    {!selectedOpponent ? (
                        <div className="p-12 text-center text-secondary-400">
                            <span className="text-4xl mb-3 block">👈</span>
                            <p>Select a school to view their roster</p>
                        </div>
                    ) : (
                        <div>
                            {/* Header */}
                            <div className="p-4 border-b border-secondary-700">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-bold text-white">
                                            {selectedOpponent.name}
                                        </h2>
                                        {selectedOpponent.location && (
                                            <p className="text-sm text-secondary-400">
                                                📍 {selectedOpponent.location}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-medium">
                                            <span className="text-green-400">{selectedOpponent.totalWins}</span>
                                            <span className="text-secondary-500"> - </span>
                                            <span className="text-red-400">{selectedOpponent.totalLosses}</span>
                                        </div>
                                        <div className="text-xs text-secondary-500">
                                            {Math.round(selectedOpponent.winRate * 100)}% win rate
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2 mt-3">
                                    {selectedOpponent.formats.map(f => (
                                        <span key={f} className="text-xs px-2 py-1 bg-blue-500/20 text-blue-400 rounded">
                                            {FORMAT_LABELS[f] || f}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Players */}
                            <div className="p-4">
                                <h3 className="text-sm font-medium text-secondary-300 mb-3">
                                    Player Roster ({players.length})
                                </h3>
                                {loadingPlayers ? (
                                    <div className="py-8 text-center">
                                        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                                    </div>
                                ) : players.length === 0 ? (
                                    <p className="text-secondary-500 text-sm">No players found</p>
                                ) : (
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {players.map(player => (
                                            <div
                                                key={player.id}
                                                className="p-3 bg-secondary-900 rounded-lg"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="font-medium text-white">
                                                            {player.gamerTag}
                                                        </p>
                                                        {player.fullName && (
                                                            <p className="text-xs text-secondary-500">
                                                                {player.fullName}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="text-right text-sm">
                                                        <div>
                                                            <span className="text-green-400">{player.wins}W</span>
                                                            <span className="text-secondary-500"> / </span>
                                                            <span className="text-red-400">{player.losses}L</span>
                                                        </div>
                                                        {player.knockOuts > 0 && (
                                                            <div className="text-xs text-secondary-400">
                                                                {player.knockOuts} KOs · {player.falls} falls
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                {player.discordName && (
                                                    <p className="text-xs text-purple-400 mt-1">
                                                        Discord: {player.discordName}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
